import { traverseWithCb, merge, minusKeys } from './_util'

function getExtendedCheckedKeySet(checkedKeys, treeStore) {
  const visitedKeysSet = new Set()
  const extendedKeysSet = new Set(checkedKeys)
  checkedKeys.forEach((key) => {
    const checkedTreeNode = treeStore.getNode(key)
    if (checkedTreeNode) {
      traverseWithCb(checkedTreeNode, (node) => {
        if (node.disabled) return 'stop'
        if (visitedKeysSet.has(node.key)) return
        visitedKeysSet.add(node.key)
        extendedKeysSet.add(node.key)
      })
    }
  })
  return extendedKeysSet
}

function getAvailableAscendantKeys(uncheckedKeys, treeStore) {
  const availableAscendantKeysSet = new Set()
  uncheckedKeys.forEach((key) => {
    const uncheckedTreeNode = treeStore.getNode(key)
    if (uncheckedTreeNode) {
      let node = uncheckedTreeNode.parent
      while (node) {
        if (node.disabled) break
        if (availableAscendantKeysSet.has(node.key)) break
        availableAscendantKeysSet.add(node.key)
        node = node.parent
      }
    }
  })
  return availableAscendantKeysSet
}

function getExtendedCheckedKeySetAfterCheck(checkedKeys, keysToCheck, treeStore) {
  return getExtendedCheckedKeySet(checkedKeys ? checkedKeys.concat(keysToCheck || []) : keysToCheck, treeStore)
}

function getExtendedCheckedKeySetAfterUnCheck(checkedKeys, keysToUnCheck, treeStore) {
  const extendedCheckedKeySet = getExtendedCheckedKeySet(checkedKeys, treeStore)
  const extendedKeysToUnCheckSet = getExtendedCheckedKeySet(keysToUnCheck, treeStore)
  const availableAscendantKeysSet = getAvailableAscendantKeys(keysToUnCheck || [], treeStore)

  extendedCheckedKeySet.forEach((key) => {
    if (extendedKeysToUnCheckSet.has(key) || availableAscendantKeysSet.has(key)) {
      extendedCheckedKeySet.delete(key)
    }
  })
  return extendedCheckedKeySet
}

export function getCheckedKeys(options, treeStore) {
  const { checkedKeys, indeterminateKeys, keysToCheck, keysToUnCheck, cascade, checkStrategy, leafOnly } = options
  if (!cascade) {
    if (keysToCheck) {
      return {
        checkedKeys: Array.from(merge(checkedKeys, keysToCheck) || []),
        indeterminateKeys: Array.from(indeterminateKeys || [])
      }
    } else if (keysToUnCheck) {
      return {
        checkedKeys: Array.from(minusKeys(checkedKeys, keysToUnCheck) || []),
        indeterminateKeys: Array.from(indeterminateKeys || [])
      }
    }
    return {
      checkedKeys: Array.from(checkedKeys || []),
      indeterminateKeys: Array.from(indeterminateKeys || [])
    }
  }
  const { levelTreeMap } = treeStore
  let extendedCheckedKeysSet
  if (keysToCheck) {
    extendedCheckedKeysSet = getExtendedCheckedKeySetAfterCheck(checkedKeys, keysToCheck, treeStore)
  } else if (keysToUnCheck) {
    extendedCheckedKeysSet = getExtendedCheckedKeySetAfterUnCheck(checkedKeys, keysToUnCheck, treeStore)
  } else {
    extendedCheckedKeysSet = getExtendedCheckedKeySet(checkedKeys, treeStore)
  }
  const checkStrategyIsParent = checkStrategy === 'parent'
  const checkStrategyIsChild = checkStrategy === 'child' || leafOnly

  const syntheticCheckedKeysSet = extendedCheckedKeysSet
  const syntheticIndeterminateKeysSet = new Set()
  const maxLevel = treeStore.maxLevel
  for (let level = maxLevel; level >= 0; level--) {
    const levelIsZero = level === 0
    const levelNodes = levelTreeMap.get(level) || []
    for (const node of levelNodes) {
      if (node.isLeaf) continue
      const { key: levelNodeKey } = node
      const shallowLoaded = Array.isArray(node.children)
      if (checkStrategyIsChild && shallowLoaded) {
        node.children.forEach((child) => {
          if (
            !child.disabled &&
            !child.isLeaf &&
            Array.isArray(child.children) &&
            syntheticCheckedKeysSet.has(child.key)
          ) {
            syntheticCheckedKeysSet.delete(child.key)
          }
        })
      }
      if (node.disabled || !shallowLoaded) continue
      let fullyChecked = true
      let partialChecked = false
      let allDisabled = true
      for (const childNode of node.children) {
        if (childNode.disabled) continue
        if (allDisabled) allDisabled = false
        if (syntheticCheckedKeysSet.has(childNode.key)) {
          partialChecked = true
        } else if (syntheticIndeterminateKeysSet.has(childNode.key)) {
          partialChecked = true
          fullyChecked = false
          break
        } else {
          fullyChecked = false
          if (partialChecked) {
            break
          }
        }
      }
      if (fullyChecked && !allDisabled) {
        if (checkStrategyIsParent) {
          node.children.forEach((child) => {
            if (!child.disabled && syntheticCheckedKeysSet.has(child.key)) {
              syntheticCheckedKeysSet.delete(child.key)
            }
          })
        }
        syntheticCheckedKeysSet.add(levelNodeKey)
      } else if (partialChecked) {
        syntheticIndeterminateKeysSet.add(levelNodeKey)
      }
      if (levelIsZero && checkStrategyIsChild && syntheticCheckedKeysSet.has(levelNodeKey)) {
        syntheticCheckedKeysSet.delete(levelNodeKey)
      }
    }
  }
  return {
    checkedKeys: Array.from(syntheticCheckedKeysSet),
    indeterminateKeys: Array.from(syntheticIndeterminateKeysSet)
  }
}



// WEBPACK FOOTER //
// ./src/layout/components/tree/tree-store/check.js